import { z } from 'zod';

/**
 * Evidence embedding contracts (DEVOP-33 / ADR 0086).
 *
 * The vector-RAG layer chunks a fetched source's full text and embeds each chunk into Supabase
 * pgvector, so extraction can retrieve the passages most relevant to a stage's metric question
 * instead of reading a blind 20k-char prefix. These are the boundary shapes for that data:
 *   - {@link EvidenceChunkSchema}        — a chunk of source text, before embedding.
 *   - {@link EvidenceEmbeddingRowSchema} — a persisted chunk + its vector (owner-scoped row).
 *   - {@link EvidenceChunkMatchSchema}   — one nearest-neighbour hit from a similarity query.
 *
 * Schema-first (parse, don't validate — ADR 0005). The vector is a plain `number[]` on the wire;
 * pgvector's `vector(N)` is a storage detail the gateway maps to/from this array. The embedding
 * MODEL + its dimension are pinned per row for lineage (a re-embed at a new model is a new row set).
 */

/** Bump when the persisted embedding-row shape changes (parse-on-read gate). */
export const EVIDENCE_EMBEDDING_SCHEMA_VERSION = 1 as const;

/** Max chars in one chunk's text — a generous ceiling; the chunker targets well under this. */
export const EVIDENCE_CHUNK_MAX_CHARS = 8_000;

/** A chunk of a source's text, addressed by its position in the source (before embedding). */
export const EvidenceChunkSchema = z.object({
  /** The evidence source this chunk came from. */
  sourceId: z.string().min(1).max(64),
  /** 0-based position of the chunk within the source's chunk sequence. */
  chunkIndex: z.number().int().nonnegative(),
  /** The chunk text (verbatim slice of the source's fetched text). */
  content: z.string().min(1).max(EVIDENCE_CHUNK_MAX_CHARS),
  /** Approximate token count (for cost/telemetry); absent when not measured. */
  tokenCount: z.number().int().nonnegative().optional(),
});
export type EvidenceChunk = z.infer<typeof EvidenceChunkSchema>;

/** One embedding vector — a non-empty array of finite numbers (length = the model's dimension). */
export const EmbeddingVectorSchema = z.array(z.number().finite()).min(1).max(4096);
export type EmbeddingVector = z.infer<typeof EmbeddingVectorSchema>;

/**
 * A persisted embedding row: a chunk plus its vector and storage identity. Owner-scoped, linked to
 * a source (and its idea) for cascade + owner filtering, exactly like every other evidence row.
 */
export const EvidenceEmbeddingRowSchema = EvidenceChunkSchema.extend({
  id: z.string().min(1).max(64),
  ownerId: z.string().min(1).max(64),
  ideaId: z.string().min(1).max(64),
  /** The embedding vector (length = {@link dimensions}). */
  embedding: EmbeddingVectorSchema,
  /** Output vector length — pins the pgvector column width; guards a model/dimension change. */
  dimensions: z.number().int().positive(),
  /** The embedding model that produced the vector (lineage), e.g. `voyage-3.5-lite`. */
  model: z.string().min(1).max(120),
  schemaVersion: z.literal(EVIDENCE_EMBEDDING_SCHEMA_VERSION),
  /** Epoch ms the row was written (injected clock — ADR 0002). */
  createdAt: z.number().finite(),
});
export type EvidenceEmbeddingRow = z.infer<typeof EvidenceEmbeddingRowSchema>;

/**
 * One nearest-neighbour hit from a similarity query — the matched chunk plus its cosine similarity
 * (1 = identical direction, 0 = orthogonal). The vector itself is not returned (only what a caller
 * needs to feed extraction: which source, which passage, and how close).
 */
export const EvidenceChunkMatchSchema = z.object({
  sourceId: z.string().min(1).max(64),
  chunkIndex: z.number().int().nonnegative(),
  content: z.string().min(1).max(EVIDENCE_CHUNK_MAX_CHARS),
  /** Cosine similarity in [0,1]; higher is closer. */
  similarity: z.number().finite(),
});
export type EvidenceChunkMatch = z.infer<typeof EvidenceChunkMatchSchema>;
